<!------------------------------------------------------------------------------
  - Copyright (c) 2024. dnote.ai
  -
  - @project DNOTE.AI-Frontend
  - @author dnote.ai
  - @copyright Dnote.ai All rights reserved
  ----------------------------------------------------------------------------->
<template>
    <DashboardCard header-title="Subscription Report"
    :refreshButton="true" @refresh="resetForm(true)">

        <!-- TABLE -->
        <PaginatedTable
            :paginated="false"
            :showElementsPerPage="true"
            :table-data="tableData"
            :table-headers="tableHeaders"
            :extra-inputs="extraInputs"
            :searchModel="searchModel"
            :date-search="false"
            @search="makeSearch"
            @sort="(newContent)=>tableData={...newContent}"
            @next="(params)=>loadPage(true, params)"
            @previous="(params)=>loadPage(false, params)"
        />
    </DashboardCard>

    <!-- 🔹 PRINTABLE PDF CONTAINER (ONLY FOR PDF / PRINT) -->
    <div class="d-none" :id="reportPageId">
        <div :style="page">

            <!-- HEADER -->
            <div :style="pageHeader">

                <!-- LEFT -->
                <div style="width:25%; padding:10px; text-transform:uppercase;">
                    <div style="font-weight:bold;">
                        {{ loggedInUser ? loggedInUser.username : '---' }}
                    </div>
                    <div style="font-size:12px;" v-if="selectedCategories">
                        Categories : {{ selectedCategories }}
                    </div>
                    <div style="font-size:12px;" v-if="selectedSubscriptions">
                        Subscriptions : {{ selectedSubscriptions }}
                    </div>
                    <div style="font-size:12px;" v-if="searchModel.district">
                        District : {{ searchModel.district }}
                    </div>
                    <div style="font-size:12px;" v-if="searchModel.pincode">
                        Pincode : {{ searchModel.pincode }}
                    </div>
                    <div style="font-size:12px;" v-if="selectedLanguages">
                        Languages : {{ selectedLanguages }}
                    </div>
                    <div style="font-size:12px;" v-if="searchModel.phone">
                        Phone : {{ searchModel.phone }}
                    </div>
                    <div style="font-size:12px;" v-if="searchModel.subscriptionDeadline">
                        Deadline : {{ getFilterLabel() }}
                    </div>
                </div>

                <!-- CENTER -->
                <div style="width:50%; text-align:center; padding:10px;">
                    <h4 style="margin-bottom:0;">
                        {{ institution ? institution.name.toUpperCase() : '---' }}
                    </h4>
                    <div style="font-weight:bold;">SUBSCRIPTION REPORT</div>
                </div>

                <!-- RIGHT -->
                <div style="width:25%; text-align:right; padding:10px;">
                    {{ getDateFromTimeStamp(new Date(), true) }}
                </div>
            </div>

            <!-- TABLE (FULL DATA for PDF) -->
            <PaginatedTable
                :table-data="allTableData"
                :table-headers="tableHeaders"
                :tablePrintable="true"
                :paginated="true"
                :hidePageControls="true"
                :rowCounter="true"
            />
        </div>
    </div>

    <!-- 🔹 BATCH PROGRESS BAR (shown during export/pdf load) -->
    <div v-if="batchLoading" class="row pt-2 px-3">
        <div class="col-12">
            <div style="background:#f0f0f0; border-radius:6px; overflow:hidden; height:18px;">
                <div
                    :style="{
                        width: batchProgress + '%',
                        background: '#3b82f6',
                        height: '100%',
                        transition: 'width 0.3s'
                    }"
                ></div>
            </div>
            <div style="font-size:12px; color:#555; margin-top:4px; text-align:center;">
                Loading {{ batchLoaded }} / {{ tableData.totalElements }} records... ({{ batchProgress }}%)
            </div>
        </div>
    </div>

    <!-- 🔹 UI ACTION BUTTONS -->
    <div class="row pt-3 px-3" v-if="tableData.totalElements > 0">

        <div :class="exportable ? 'col-md-6 mb-3' : 'col-md-12 mb-3'">
            <Button
                :label="printButtonLabel"
                icon="pi pi-print"
                class="w-100"
                :disabled="batchLoading"
                @click="makePdf(false)"
            />
        </div>

        <div class="col-md-6 mb-3" v-if="exportable">
            <Button
                label="Export Excel"
                icon="pi pi-file-export"
                severity="danger"
                class="w-100"
                :disabled="batchLoading"
                @click="exportExcel"
            />
        </div>

    </div>
</template>

<script>
import PaginatedTable from "@/dashboard/utils/paginator/PaginatedTable.vue";
import DashboardCard from "@/dashboard/containers/DashboardCard.vue";
import { getCountries } from "@/dashboard/utils/countries";
import { AVAILABLE_LANGUAGES, INDIA_STATES } from "@/dashboard/organization/Organization";
import Button from "primevue/button";
import { SUBSCRIPTION_PLANS } from "@/dashboard/organization/Organization";
import { getDateFromTimeStamp } from "@/utils/AppFx";

// ✅ Batch size — fetch 200 records per API call
//    200 × 13 batches = 2600 records, well under SQL Server's 2100 param limit per query
const FETCH_BATCH_SIZE = 200;

export default {
    name: "SubscriptionReport",

    components: {
        PaginatedTable,
        DashboardCard,
        Button,
    },

    data() {
        return {
            /** SEARCH MODEL */
            searchModel: {
                query: null,
                partnerCodes: null,
                phone: null,
                alternatePhone: null,
                countries: [],
                states: [],
                state: null,
                pincode: null,
                district: null,
                categories: null,
                active: null,
                deadline: null,
                languages: null,
                page: 1,
                size: 10,
            },

            reportPageId: Math.random().toString(36).substring(2),
            page: { width: "100%" },
            pageHeader: {
                display: "flex",
                width: "100%",
                backgroundColor: "white",
                color: "#000"
            },

            // ✅ Batch loading state
            batchLoading: false,
            batchLoaded: 0,
            batchProgress: 0,

            /** FILTER INPUTS */
            extraInputs: [
                {
                    type: "MULTISELECT",
                    model: "countries",
                    placeholder: "Country",
                    styleClass: "col-md-4 mb-3",
                    options: [...getCountries()]
                },
                {
                    type: "MULTISELECT",
                    model: "states",
                    placeholder: "States",
                    styleClass: "col-md-4 mb-3",
                    options: [...INDIA_STATES],
                    shownIf: {
                        model: "countries",
                        values: ["IN"],
                        exactMatch: true,
                        onShowSet: { model: "state", value: null }
                    }
                },
                {
                    type: "TEXT",
                    model: "state",
                    placeholder: "State",
                    styleClass: "col-md-4 mb-3",
                    hiddenIf: {
                        model: "countries",
                        values: ["IN"],
                        exactMatch: true,
                        onShowSet: { model: "states", value: null }
                    }
                },
                { type: "TEXT", model: "district", placeholder: "District", styleClass: "col-md-4 mb-3" },
                { type: "TEXT", model: "pincode", placeholder: "Pincode", styleClass: "col-md-4 mb-3" },
                {
                    type: "MULTISELECT",
                    model: "languages",
                    placeholder: "Languages",
                    styleClass: "col-md-4 mb-3",
                    options: AVAILABLE_LANGUAGES
                },
                {
                    type: "MULTISELECT",
                    model: "categories",
                    placeholder: "Category",
                    styleClass: "col-md-4 mb-3",
                    options: []
                },
                { type: "TEXT", model: "phone", placeholder: "Phone Number...", styleClass: "col-md-4 mb-3" },
                {
                    type: "TEXTAREA",
                    model: "partnerCodes",
                    placeholder: "Partner Codes...",
                    styleClass: "col-md-12 mb-3"
                },
                {
                    type: "SELECT",
                    model: "active",
                    styleClass: "col-md-4 mb-3",
                    placeholder: "Status",
                    options: [
                        { label: "All", value: "All" },
                        { label: "Active", value: true },
                        { label: "Not Active", value: false }
                    ]
                },
                {
                    type: "MULTISELECT",
                    model: "subscription",
                    placeholder: "Subscription",
                    styleClass: "col-md-4 mb-3",
                    options: [...SUBSCRIPTION_PLANS]
                },
                {
                    type: "SELECT",
                    model: "subscriptionDeadline",
                    placeholder: "Subscription Deadline",
                    styleClass: "col-md-4 mb-3",
                    options: [
                        { label: "Today",          value: "TODAY"        },
                        { label: "Next 7 Days",    value: "NEXT_7_DAYS"  },
                        { label: "Next 1 Month",   value: "NEXT_1_MONTH" },
                        { label: "Next 3 Months",  value: "NEXT_3_MONTH" },
                        { label: "Next 6 Months",  value: "NEXT_6_MONTH" },
                        { label: "Next 1 Year",    value: "NEXT_1_YEAR"  },
                        { label: "Custom Range",   value: "CUSTOM"       }
                    ],
                    triggerChange: true
                },
                {
                    type: "DATE",
                    model: "deadlineFrom",
                    placeholder: "Deadline From",
                    format: "dd/mm/yy",
                    styleClass: "col-md-4 mb-3",
                    shownIf: { model: "subscriptionDeadline", values: ["CUSTOM"] }
                },
                {
                    type: "DATE",
                    model: "deadlineTo",
                    placeholder: "Deadline To",
                    format: "dd/mm/yy",
                    styleClass: "col-md-4 mb-3",
                    shownIf: { model: "subscriptionDeadline", values: ["CUSTOM"] }
                },
            ],

            /** TABLE HEADERS */
            tableHeaders: {
                "PARTNER CODE": { rows: [{ type: "String",      attribute: "code"                         }] },
                "PARTNER NAME": { rows: [{ type: "String",      attribute: "fullName"                     }] },
                "CATEGORY":     { rows: [{ type: "String",      attribute: "categoryName"                 }] },
                "ADDRESS":      { rows: [{ type: "String",      attribute: "fullAddress"                  }] },
                "TYPE":         { rows: [{ type: "String",      attribute: "type"                         }] },
                "PHONE":        { rows: [{ type: "PhoneNumber", attribute: ["phoneCode", "phone"]          }] },
                "START DATE":   { rows: [{ type: "DateTime",    attribute: "startDate"                    }] },
                "END DATE":     { rows: [{ type: "DateTime",    attribute: "endDate"                      }] }
            },

            tableData: {
                content: [],
                totalElements: 0,
                totalPages: 0,
                currentPage: 0
            },
            allTableData: {
                content: [],
                totalElements: 0,
                totalPages: 0,
                currentPage: 0
            },
        };
    },

    computed: {
        exportable() {
            return this.tableData.totalElements > 0;
        },
        printButtonLabel() {
            return "Export PDF";
        },
        loggedInUser() {
            return this.$store.getters.getLoggedInUser;
        },
        institution() {
            const user = this.$store.getters.getLoggedInUser;
            if (!user) return null;
            return user.institution || this.$store.getters.getInstitution;
        },
        selectedCategories() {
            if (!this.searchModel.categories) return null;
            const categoryInput = this.extraInputs.find(x => x.model === "categories");
            return this.searchModel.categories
                .map(id => {
                    const opt = categoryInput.options.find(o => o.value === id);
                    return opt ? opt.label : null;
                })
                .filter(Boolean)
                .join(", ");
        },
        selectedSubscriptions() {
            if (!this.searchModel.subscription) return null;
            return this.searchModel.subscription.join(", ");
        },
        selectedLanguages() {
            if (!this.searchModel.languages) return null;
            return this.searchModel.languages.join(", ");
        }
    },

    methods: {
        getDateFromTimeStamp,

        /** LOAD CATEGORY LIST */
        loadCategories() {
            const body = this.$root.addInstitutionId({});
            this.$api.post("/category/get", body).then(res => {
                const categoryInput = this.extraInputs.find(x => x.model === "categories");
                categoryInput.options = res.map(c => ({
                    label: c.name.toUpperCase(),
                    value: c.id
                }));
            });
        },

        getFilterLabel() {
            const f = this.searchModel.subscriptionDeadline;
            const formatDate = (d) => {
                if (!d) return "";
                let date = new Date(d);
                let day   = String(date.getDate()).padStart(2, "0");
                let month = String(date.getMonth() + 1).padStart(2, "0");
                let year  = date.getFullYear();
                return `${day}/${month}/${year}`;
            };
            let today = new Date();
            let from  = new Date(today);
            let to    = new Date(today);
            if (!f) return "All";
            switch (f) {
                case "TODAY":        break;
                case "NEXT_7_DAYS":  to.setDate(to.getDate() + 7);      break;
                case "NEXT_1_MONTH": to.setMonth(to.getMonth() + 1);    break;
                case "NEXT_3_MONTH": to.setMonth(to.getMonth() + 3);    break;
                case "NEXT_6_MONTH": to.setMonth(to.getMonth() + 6);    break;
                case "NEXT_1_YEAR":  to.setFullYear(to.getFullYear()+1); break;
                case "CUSTOM":
                    return `${formatDate(this.searchModel.deadlineFrom)} – ${formatDate(this.searchModel.deadlineTo)}`;
            }
            return `${formatDate(from)} – ${formatDate(to)}`;
        },

        resetForm(reload = true) {
            this.searchModel = {
                query: null,
                partnerCodes: null,
                phone: null,
                alternatePhone: null,
                countries: [],
                states: [],
                state: null,
                pincode: null,
                district: null,
                categories: null,
                subscription: null,
                subscriptionDeadline: null,
                deadlineFrom: null,
                deadlineTo: null,
                active: null,
                languages: null,
                page: 1,
                size: 10
            };
            this.tableData = { content: [], totalElements: 0, totalPages: 0, currentPage: 0 };
            this.allTableData = { content: [], totalElements: 0, totalPages: 0, currentPage: 0 };
            this.batchLoading  = false;
            this.batchLoaded   = 0;
            this.batchProgress = 0;
            this.loadCategories();
        },

        /** SEARCH CLICK */
        makeSearch(params) {
            if (params.deadlineFrom) {
                let today    = new Date(); today.setHours(0,0,0,0);
                let fromDate = new Date(params.deadlineFrom); fromDate.setHours(0,0,0,0);
                if (fromDate < today) {
                    this.$toast.add({ severity: "warn", summary: "Invalid Date", detail: "Deadline From cannot be a past date", life: 4000 });
                    return;
                }
            }
            if (params.deadlineFrom && params.deadlineTo) {
                if (new Date(params.deadlineFrom) > new Date(params.deadlineTo)) {
                    this.$toast.add({ severity: "warn", summary: "Invalid Date Range", detail: "Deadline From cannot be greater than Deadline To", life: 4000 });
                    return;
                }
            }
            params.page = 1;
            if (params.active === "ALL" || params.active === "All") params.active = null;
            const originalPartnerCodes = params.partnerCodes;
            if (params.partnerCodes) {
                params.partnerCodes = Array.isArray(params.partnerCodes)
                    ? params.partnerCodes
                    : params.partnerCodes.split(/[\n,]+/).map(code => code.trim()).filter(code => code.length > 0);
            }
            this.searchModel = { ...params };
            this.searchModel.partnerCodes = originalPartnerCodes;
            this.fetchAll({ ...params });
        },

        /** PAGINATION */
        loadPage(isNext, params) {
            let data = {
                ...this.searchModel,
                page:        isNext ? this.searchModel.page + 1 : this.searchModel.page - 1,
                currentPage: isNext ? this.searchModel.page + 2 : this.searchModel.page,
                size:        params.size
            };
            this.searchModel = { ...data };
        },

        // ================================================================
        // ✅ CLEAN EXPORT PARAMS HELPER
        //    Strips UI-only fields, normalizes partnerCodes & active
        // ================================================================
        buildExportParams(overrides = {}) {
            const p = { ...this.searchModel, ...overrides };
            if (p.active === "ALL" || p.active === "All") p.active = null;
            if (p.partnerCodes && !Array.isArray(p.partnerCodes)) {
                p.partnerCodes = p.partnerCodes
                    .split(/[\n,]+/)
                    .map(v => v.trim())
                    .filter(Boolean);
            }
            return p;
        },

        // ================================================================
        // ✅ MAP RAW ROW → display row (shared by fetchAll & batch export)
        // ================================================================
        mapRow(row) {
            let subs = row.extra?.subscriptions ?? [];
            let selectedCats = this.searchModel.categories;
            if (selectedCats && !Array.isArray(selectedCats)) selectedCats = [selectedCats];
            let selectedSub = (selectedCats?.length)
                ? subs.find(s => selectedCats.includes(s.categoryId))
                : subs[0] ?? null;
            let categoryName = "";
            if (selectedSub) {
                let opt = this.extraInputs.find(x => x.model === "categories")
                    ?.options.find(c => c.value === selectedSub.categoryId);
                if (opt) categoryName = opt.label;
            }
            return {
                ...row,
                type:        selectedSub ? selectedSub.type      : "N/A",
                categoryName,
                fullAddress: [row.addressLine1, row.addressLine2, row.addressLine3, row.district, row.pincode]
                    .filter(v => v && v.toString().trim() !== "").join(", "),
                startDate:   selectedSub ? selectedSub.startDate : null,
                endDate:     selectedSub ? selectedSub.endDate   : null
            };
        },

        // ================================================================
        // ✅ FETCH ALL — first gets count (size:1), then fetches page-by-page
        //    in FETCH_BATCH_SIZE (200) chunks to avoid SQL Server 2100 limit
        // ================================================================
        fetchAll(params) {
            this.$store.commit("setLoading", true);

            // Step 1: Get total count with size:1
            const firstPayload = this.$root.addInstitutionId({ ...this.buildExportParams(params), page: 0, size: 1 });

            this.$api.post("/institution/subscription/report", firstPayload)
                .then(firstRes => {
                    const total = firstRes.totalElements;
                    if (!total || total <= 0) {
                        this.tableData    = { content: [], totalElements: 0, totalPages: 0, currentPage: 0 };
                        this.allTableData = { content: [], totalElements: 0, totalPages: 0, currentPage: 0 };
                        this.$store.commit("setLoading", false);
                        return;
                    }

                    // Step 2: Fetch all records in batches of FETCH_BATCH_SIZE
                    this.fetchInBatches(params, total)
                        .then(allMapped => {
                            const size = this.searchModel.size || 10;
                            this.allTableData = {
                                content:       allMapped,
                                totalElements: allMapped.length,
                                totalPages:    1,
                                currentPage:   0
                            };
                            this.tableData = {
                                content:       allMapped,
                                totalElements: allMapped.length,
                                totalPages:    Math.ceil(allMapped.length / size),
                                currentPage:   1,
                                size
                            };
                            this.$store.commit("setLoading", false);
                        })
                        .catch(err => this.handleError(err, "Subscription Report"));
                })
                .catch(err => this.handleError(err, "Subscription Report"));
        },

        // ================================================================
        // ✅ CORE BATCH FETCHER
        //    Splits total records into FETCH_BATCH_SIZE pages,
        //    fetches sequentially, shows progress bar
        // ================================================================
        async fetchInBatches(params, total) {
            const cleanParams  = this.buildExportParams(params);
            const totalBatches = Math.ceil(total / FETCH_BATCH_SIZE);
            const allMapped    = [];

            this.batchLoading  = true;
            this.batchLoaded   = 0;
            this.batchProgress = 0;

            for (let batchIndex = 0; batchIndex < totalBatches; batchIndex++) {
                const payload = this.$root.addInstitutionId({
                    ...cleanParams,
                    page: batchIndex,
                    size: FETCH_BATCH_SIZE
                });

                const res = await this.$api.post("/institution/subscription/report", payload);

                if (res && res.content) {
                    const mapped = res.content.map(row => this.mapRow(row));
                    allMapped.push(...mapped);
                }

                // ✅ Update progress bar
                this.batchLoaded   = Math.min(allMapped.length, total);
                this.batchProgress = Math.round((this.batchLoaded / total) * 100);
            }

            this.batchLoading  = false;
            this.batchLoaded   = 0;
            this.batchProgress = 0;

            return allMapped;
        },

        // ================================================================
        // ✅ EXPORT EXCEL — uses allTableData (already fetched in batches)
        //    Falls back to batch fetch if allTableData is empty
        // ================================================================
        async exportExcel() {
            this.logReportAction("SUBSCRIPTION_EXCEL");

            let data = this.allTableData.content;

            // Safety: if allTableData not loaded yet, fetch in batches
            if (!data || data.length === 0) {
                this.$store.commit("setLoading", true);
                try {
                    data = await this.fetchInBatches(this.searchModel, this.tableData.totalElements);
                    this.allTableData = { content: data, totalElements: data.length, totalPages: 1, currentPage: 0 };
                } catch (err) {
                    this.handleError(err, "Export Excel");
                    return;
                } finally {
                    this.$store.commit("setLoading", false);
                }
            }

            const headers = Object.keys(this.tableHeaders);

            const rows = data.map(row =>
                headers.map(h => {
                    const col = this.tableHeaders[h].rows[0];
                    let value = "";

                    if (h === "ADDRESS") {
                        value = row.fullAddress ?? "";
                    } else if (h === "CATEGORY") {
                        value = row.categoryName ?? "";
                    } else if (col.type === "String") {
                        value = row[col.attribute] ?? "";
                    } else if (col.type === "PhoneNumber") {
                        const code = row[col.attribute[0]] ?? "";
                        const ph   = row[col.attribute[1]] ?? "";
                        value = ph ? `+${code}${ph}` : "";
                    } else if (col.type === "DateTime") {
                        value = row[col.attribute]
                            ? new Date(row[col.attribute]).toLocaleDateString()
                            : "";
                    }

                    return String(value).replace(/,/g, ";");
                })
            );

            const csvRows = [headers.join(","), ...rows.map(r => r.join(","))];
            const csv     = csvRows.join("\n").toUpperCase();

            const blob = new Blob([csv], { type: "text/csv" });
            const a    = document.createElement("a");
            a.download = "SUBSCRIPTION_REPORT.csv";
            a.href     = window.URL.createObjectURL(blob);
            a.click();
        },

        // ================================================================
        // ✅ PDF GENERATION — uses allTableData (already batch-fetched)
        //    Falls back to batch fetch if needed
        // ================================================================
        async makePdf() {
            this.logReportAction("SUBSCRIPTION_PDF");
            this.$store.commit("setLoading", true);

            let data = this.allTableData.content;

            // Safety: if allTableData not loaded yet, fetch in batches
            if (!data || data.length === 0) {
                try {
                    data = await this.fetchInBatches(this.searchModel, this.tableData.totalElements);
                    this.allTableData = { content: data, totalElements: data.length, totalPages: 1, currentPage: 0 };
                } catch (err) {
                    this.handleError(err, "Generate PDF");
                    return;
                }
            }

            // Ensure allTableData is set for PaginatedTable in PDF template
            this.allTableData = {
                content:       data,
                totalElements: data.length,
                totalPages:    1,
                currentPage:   0
            };

            this.$store.commit("setLoading", false);

            // Double nextTick required for DOM to render PaginatedTable
            this.$nextTick(() => {
                this.$nextTick(() => {
                    this.downloadAsPDF();
                });
            });
        },

        logReportAction(action) {
            if (!this.institution) return;
            this.$api.post("/institution/report-action", {
                action: action,
                institutionId: this.institution.id
            }).catch(() => {});
        },

        downloadAsPDF() {
            const htmlElement = document.getElementById(this.reportPageId);
            if (!htmlElement) return;

            htmlElement.style.display = "block";
            const content = htmlElement.outerHTML;
            htmlElement.style.display = "none";

            let disp_setting  = "toolbar=yes,location=no,";
                disp_setting += "directories=yes,menubar=yes,";
                disp_setting += "scrollbars=yes,width=650,height=600,left=100,top=25";

            let docprint = window.open("", "", disp_setting);
            if (!docprint) { alert("Popup blocked"); return; }

            docprint.document.open();
            docprint.document.write(`
<!DOCTYPE html>
<html>
<head>
<title>Subscription Report</title>
<style>
@page { size: A4; margin: 8mm; }
body  { margin: 0; padding: 0; width: 100%; }
table {
  width: 100% !important;
  border-collapse: collapse;
  margin: 0;
  font-family: Arial, Verdana, Geneva, sans-serif;
  font-size: 12px;
  text-transform: uppercase;
}
th { border: 1px solid #444; padding: 5px; text-align: left; font-weight: bold; color: #000; }
td { border: 1px solid #444; padding: 5px; text-align: left; color: #000; }
tr { page-break-inside: avoid; }
</style>
</head>
<body onload="window.print(); window.close();">
  ${content}
</body>
</html>`);
            docprint.document.close();
            docprint.focus();
        },

        // ================================================================
        // ✅ CENTRAL ERROR HANDLER
        // ================================================================
        handleError(err, context = "") {
            let message = "Something went wrong";
            if (err?.response?.data?.message) message = err.response.data.message;
            this.$toast.add({ severity: "error", summary: context, detail: message, life: 4000 });
            this.$store.commit("setLoading", false);
            this.batchLoading  = false;
            this.batchLoaded   = 0;
            this.batchProgress = 0;
        }
    },

    mounted() {
        this.loadCategories();
    }
};
</script>

<style scoped lang="scss"></style>
